import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0503 from "../lesson-03/lesson";

const linkLesson0503 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0503.id);

const linkLesson0515 = getLinkForCourseLesson(
  "53e2fab0-edf6-4634-a20e-ae0cdfe73cba",
  "3c7a1a40-617a-4084-b637-5ee63d738bec"
);

const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>This lesson makes an overview of various kinds of absorbers, in particular their physical functioning principle and efficiency of porous, membrane and resonator absorbers. Other absorber types are introduced briefly as well.
<h3 class=sectionHead>Introduction</h3>
<p>When thinking about construction elements that can be used for controlling the acoustic behaviour of a closed space, the first one that comes to mind is a sound absorber. Closed spaces built nowadays are often very reverberant because they are built of modern building materials such as concrete, glass, plaster, gypsum boards, and ceramic tiles, all of which are hard and reflective. As such, these materials provide very little sound absorption, and acoustic finish of a building using appropriate materials and constructions is often not designed or specified.<br class=newline>Sound absorbers come in different shapes and sizes, they are built from different materials, and, consequently, they display a wide range of frequency-dependant sound absorption characteristics. No sound absorbers are capable of absorbing sound equally at all audible frequencies. Instead, each type of absorber is optimized for a specific frequency range, within which its sound absorption coefficient is higher than it is in the remainder of the audible frequency range. In very coarse terms, absorbers can be divided into low-frequency, mid-frequency, and high-frequency absorbers. To fully understand the intricacies of efficient sound absorption in specific frequency regions, the physical principles of sound absorption and the consequent construction of absorbers shall be examined in detail.
<h3 class=sectionHead>Efficiency of sound absorption</h3>
<p>To understand different types of absorbers, one must first recall what sound absorption coefficient is and how it is calculated (<a href="${linkLesson0503}" target="_blank">Lesson 5.3</a>). Sound absorbers are characterized with their frequency-dependant sound absorption coefficient as a measure of the amount of energy of an incoming sound wave that is “lost” in the absorber (in fact, the energy is converted into other forms, such as heat).<br class=newline>Another important fact that has not been mentioned yet is the formation of standing waves. When a harmonic sound wave produced continuously by a sound source propagates through space and reflects from an obstacle, the reflected wave travels back in the direction of the incident wave and interacts with it. This can be easily understood as Eqs. 10 and 11 from <a href="${linkLesson0503}" target="_blank">Lesson 5.3</a> that represent the incident and the reflected wave, respectively, are summed up:<br class=newline>
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ky</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mi>R</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ky</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   As long as the sound source produces a continuous, harmonic incident sound wave, Eq. 1 shows the change of sound pressure of the superimposed incident and reflected sound waves as a function of space and time. If only normal incidence (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   = 0) is observed, this equation can be simplified. Further simplification stems from the assumption that the surface is perfectly reflecting (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   = 1). Taking both simplifications into account, Eq. 1 can now be rewritten as:
<table class=equation>
   <tr>
      <td style="font-size: 5px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi mathvariant=italic>kx</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Eq. 2 implies that the total sound pressure reaches minimum values of zero whenever
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is equal to zero, regardless of the time-dependent part j
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   t. In other words, the pressure minima occurs at the following positions called the nodes of the standing wave:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(3)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>π</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mfrac>
               <mrow>
                  <mn>3</mn>
                  <mi>π</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mfrac>
               <mrow>
                  <mn>5</mn>
                  <mi>π</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(4)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>x</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mfrac>
               <mrow>
                  <mn>3</mn>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mfrac>
               <mrow>
                  <mn>5</mn>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>n</mi>
            <mfrac>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <mi>n</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mo class=MathClass-punc>,</mo>
            <mn>3</mn>
            <mo class=MathClass-punc>,</mo>
            <mn>5</mn>
            <mo class=MathClass-punc>,</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Analogously, at positions where the absolute value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   reaches its maximum and is equal to unity, the sound pressure also reaches its maximum value that, of course, varies in time, since the sound wave is a harmonic sine signal. These positions are called antinodes and can be calculated as:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
         </math>
      <td class=eq-no>(6)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>,</mo>
            <mi>π</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>2</mn>
            <mi>π</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>3</mn>
            <mi>π</mi>
            <mo class=MathClass-punc>,</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(7)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>x</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>,</mo>
            <mfrac>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mi>λ</mi>
            <mo class=MathClass-punc>,</mo>
            <mfrac>
               <mrow>
                  <mn>3</mn>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>n</mi>
            <mfrac>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mi>n</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>,</mo>
            <mn>1</mn>
            <mo class=MathClass-punc>,</mo>
            <mn>2</mn>
            <mo class=MathClass-punc>,</mo>
            <mn>3</mn>
            <mo class=MathClass-punc>,</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   The dynamic change of the static, atmospheric sound pressure in the audible frequency range is the phenomenon that can be heard as sound and that can be recorded using a microphone. The reduction of the energy of a sound field must be done in locations where the sound field has its maxima of kinetic energy. This kinetic energy of the air molecules is proportional to the particle velocity of the sound wave. Without going into further details, the particle velocity of the sum of the incident sound wave (travelling in the positive x-direction) and the reflected sound wave is given with Eq. 5, and this expression is similar to Eq. 2 for sound pressure. It is again a standing wave, but the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   term is substituted with the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   term, which reaches the value of zero where the absolute value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   reaches its maximum, and vice versa. In other words, the pressure nodes are the particle velocity antinodes, and vice versa.
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
            </mfrac>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi mathvariant=italic>kx</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   The resulting particle velocities of a standing sound wave at various time instances are shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-1.svg data-img-alt="Figure 1. The resulting standing sound wave particle velocity when two waves (incident and reflected) are travelling in opposite directions.">Figure 1</a>. The positions of nodes and antinodes are clearly visible and marked with red and blue dots respectively. The particle velocity is always zero in its respective nodes, and changes harmonically between its minimum and maximum value at its antinodes. Since the kinetic energy of air molecules is proportional to their particle velocity, it changes in the same manner. Therefore, if one needs to achieve sound absorption, the best positions to put absorptive material are the antinodes of particle velocity. There the kinetic energy of air molecules is the highest, and the friction between these molecules and the absorbing material is maximized as well. Consequently, the absorption of sound energy by transforming it into heat through friction is also maximized. Therefore, at a certain frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   , the particle velocity reaches its maxima at coordinates
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   =
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>5</mn>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   , etc. away from the reflecting surface (a wall or the ceiling). The relationship between the wavelength, frequency and speed of sound is given with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>c</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>f</mi>
   </math>
   .
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-1.svg alt="Figure 1. The resulting standing sound wave particle velocity when two waves (incident and reflected) are travelling in opposite directions."><figcaption class=caption>Figure 1. The resulting standing sound wave particle velocity when two waves (incident and reflected) are travelling in opposite directions.</figcaption></figure>
<p>
   Therefore, for normal incidence of a sound wave reflecting from a surface, the absorption capability of a layer of absorptive material is maximized in the antinodes of the particle velocity of the standing wave. Some sound energy is also absorbed if the absorptive material is placed somewhere between nodes and antinodes, but the absorption mechanism is not as efficient. This finding is generally valid for random incidence of a sound wave onto a reflecting surface. The smallest absorption effect is observed when the absorptive material is placed in the node of the particle velocity of the standing wave, as there is almost no movement of the air molecules. This is clearly visible in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-2.svg data-img-alt="Figure 2. Sound absorption of a thin layer of a porous material hung at a fixed distance from a reflecting wall [1].">Figure 2</a>, which presents the absorption coefficient of a thin absorptive layer, such as drapes placed at a fixed distance in front of a hard, reflective wall. The maxima of the absorption coefficient repeat at frequencies for which the distance of the absorptive material from the wall is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>5</mn>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   , etc.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-2.svg alt="Figure 2. Sound absorption of a thin layer of a porous material hung at a fixed distance from a reflecting wall [1]."><figcaption class=caption>Figure 2. Sound absorption of a thin layer of a porous material hung at a fixed distance from a reflecting wall [1].</figcaption></figure>
<h3 class=sectionHead>Types of sound absorbers</h3>
<p>
   Sound absorbers can be divided according to the physical principle exploited for absorption of sound energy. Typically, they are divided to porous absorbers, membrane (panel) absorbers and resonant (Helmholtz) absorbers, but there can be slight variations in this division. There are other types of absorbers that draw their absorption properties from other physical principles, such as microperforated absorbers, or metamaterial structures such as sonic crystals. <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-3.svg data-img-alt="Figure 3. Basic types of sound absorbers.">Figure 3</a> shows the cross-section of the basic types of sound absorbers, with indicated key components.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-3.svg alt="Figure 3. Basic types of sound absorbers."><figcaption class=caption>Figure 3. Basic types of sound absorbers.</figcaption></figure>
<p>
   Sound absorbers differ from one another by their typical absorption coefficients and the materials they are built of. A very basic qualitative sketch of the frequency-dependent sound absorption coefficient for basic absorber types is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-4.svg data-img-alt="Figure 4. Typical frequency-dependent sound absorption coefficient for basic types of absorbers.">Figure 4</a>. Typical materials used in construction of sound absorbers are listed in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/table-1.svg data-img-alt="Table 1. Typical materials used in construction of sound absorbers.">Table 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-4.svg alt="Figure 4. Typical frequency-dependent sound absorption coefficient for basic types of absorbers."><figcaption class=caption>Figure 4. Typical frequency-dependent sound absorption coefficient for basic types of absorbers.</figcaption></figure>
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Typical materials used in construction of sound absorbers.</figcaption>
      <img src=${config.ASSETS_URL}/05/08/theory/table-1.svg alt="Table 1. Typical materials used in construction of sound absorbers.">
   </figure>
</div>
<h3 class=sectionHead>Porous absorbers</h3>
<p>
   Porous absorbers are characterized by the transformation of the sound energy, i.e. the kinetic energy of the air molecules into heat due to the friction of the air molecules and the absorptive material. For a porous absorber to be effective in absorbing sound energy, it must have an open-pore structure that allows the air to pass through it. A rough assessment of whether a material is an effective porous absorber can be made simply by blowing through a sample of that material. If the air passes through it, the material is an efficient porous absorber. The opposite is also true; materials with a closed-pore structure ultimately display very poor sound absorbing properties.<br class=newline>The diameter of the fibres in the materials most commonly used for porous absorbers (mineral wool, glass wool or stone wool) is usually between 4 and 10
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μm</mi>
   </math>. The manufacturing process of mineral wool blankets ensures an anisotropic distribution of its fibres, i.e. their orientation is randomly distributed in the plane parallel to their length/width.
<h4 class=subsectionHead>Modelling porous materials</h4>
<p>Absorption characteristics of porous absorbers is evaluated using a parameter called porosity, defined as:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>σ</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>por</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>tot</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   In Eq. 6.,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>σ</mi>
   </math>
   is the porosity,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>por</mi>
         </mrow>
      </msub>
   </math>
   is the pore volume of the absorber in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   , and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>tot</mi>
         </mrow>
      </msub>
   </math>
   is the total volume of the absorber in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   . The value of porosity is usually above 0.9 (1 is the theoretical maximum) for mineral wools, foams, and felts as materials commonly used in construction of highly effective porous absorbers.<br class=newline>Another important parameter is the flow resistivity. There are a lot of models that predict the absorption behaviour of porous absorbers by using the data on their flow resistivity. It is calculated as:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Δp</mi>
               </mrow>
               <mrow>
                  <mi>v</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   In Eq. 7.,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   is the flow resistivity of the material in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Ns</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>m</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δp</mi>
   </math>
   is the static pressure difference across the porous material, and v is the velocity of the air flow through the material.<br class=newline>There are many models that describe the behaviour of porous absorbers. Due to the diversity of the fibre/pore structure and the stiffness of such materials, it can be quite challenging to predict their sound absorption coefficient.<br class=newline>According to the current models, the typical behaviour of the absorption coefficient of porous materials can be seen on an example shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-5.svg data-img-alt="Figure 5. Absorption coefficient for porous absorbers depending on their thickness in mm [3].">Figure 5</a>. The calculation is based on the so-called Delany-Bazley model and is made for a material with the flow resistivity of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>10</mn>
      <mi mathvariant=italic>kPa</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi>s</mi>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   , for normal incidence and for the material placed directly onto the hard surface (no air gap in between). It can be seen that the thickness of the material is the determining factor for the behaviour of the sound absorption coefficient. Greater thickness of the material lowers the frequency at which the sound absorption coefficient begins to monotonously decrease from its maximum value to zero. In compliance with the above considerations, the thickness of the material defines the lower cut-off frequency of a porous absorber. At this frequency, the thickness is equal to the distance of the first antinode of the particle velocity (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   from the hard surface behind the absorber). As the frequency decreases below the cut-off frequency, the antinode of particle velocity is located outside the absorbing material, and is getting farther and farther away from it, thus severely reducing the efficiency of the porous absorber.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-5.svg alt="Figure 5. Absorption coefficient for porous absorbers depending on their thickness in mm [3]."><figcaption class=caption>Figure 5. Absorption coefficient for porous absorbers depending on their thickness in mm [3].</figcaption></figure>
<p>
   <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-6.svg data-img-alt="Figure 6. Absorption coefficient for porous absorbers depending on their flow resistivity in <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>kPa</mi><mo class=MathClass-bin>⋅</mo><mi>s</mi><mo class=MathClass-bin>∕</mo><msup><mrow><mi>m</mi></mrow><mrow><mn>2</mn></mrow></msup></math> [3].">Figure 6</a> shows the influence of flow resistivity of the porous material on its absorption coefficient for a piece of such material with a constant thickness of 50 mm. The difference is not as dramatic as the one displayed for varying thicknesses. However, materials with higher flow resistivity, i.e. denser, heavier materials, exhibit higher sound absorption at low frequencies, and lower absorption at high frequencies, compared to materials with low flow resistivity.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-6.svg alt="Figure 6. Absorption coefficient for porous absorbers depending on their flow resistivity in <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>kPa</mi><mo class=MathClass-bin>⋅</mo><mi>s</mi><mo class=MathClass-bin>∕</mo><msup><mrow><mi>m</mi></mrow><mrow><mn>2</mn></mrow></msup></math> [3]."><figcaption class=caption>Figure 6. Absorption coefficient for porous absorbers depending on their flow resistivity in <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>kPa</mi><mo class=MathClass-bin>⋅</mo><mi>s</mi><mo class=MathClass-bin>∕</mo><msup><mrow><mi>m</mi></mrow><mrow><mn>2</mn></mrow></msup></math> [3].</figcaption></figure>
<p>
   As stated before, a sound wave reflected from a rigid plane is summed with the incoming wave. The resulting wave is a standing wave that has zero particle velocity, i.e. a node, at the rigid reflecting plane, and a maximum value (antinode) at the distance of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   from the reflecting plane, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-1.svg data-img-alt="Figure 1. The resulting standing sound wave particle velocity when two waves (incident and reflected) are travelling in opposite directions.">Figure 1</a>. The sound pressure displays quite the opposite behaviour – it has the maximum value (antinode) at the rigid plane, and the minimum value (node) at the distance of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   from the plane. Since the sound absorption of a porous material is based on the friction between the moving air molecules and the material itself, the particle velocity is the determining parameter. Therefore, for maximum performance, porous materials should have high porosity and optimum flow resistivity and should be placed in locations characterized with high particle velocity, namely, at distances of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>5</mn>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
   </math>
   , etc. from the reflecting plane for any given frequency of the sound wave. <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-7.svg data-img-alt="Figure 7. The optimal placement a porous absorber at the position with maximum particle velocity at various frequencies.">Figure 7</a> illustrates where a porous absorber can be placed for maximum performance, depending on the frequency of the sound wave.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-7.svg alt="Figure 7. The optimal placement a porous absorber at the position with maximum particle velocity at various frequencies."><figcaption class=caption>Figure 7. The optimal placement a porous absorber at the position with maximum particle velocity at various frequencies.</figcaption></figure>
<p>
   To extend the high performance of porous absorbers towards the low frequencies without having to resort to very thick material, a thin, porous absorber can be placed at a distance from the reflecting surface, as illustrated in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-7.svg data-img-alt="Figure 7. The optimal placement a porous absorber at the position with maximum particle velocity at various frequencies.">Figure 7</a>. An air-filled cavity is left between the porous absorber and the hard surface. Such a design is not the same as having a thicker, porous absorber that would completely fill the air cavity. Nevertheless, the decrease of absorption at higher frequencies due to the mismatch of the absorber position and the maximum of particle velocity at these frequencies is not that dramatic. <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-8.svg data-img-alt="Figure 8. Absorption coefficient for three porous absorbers with the same total construction height of 100 mm, but installed differently [3].">Figure 8</a> shows the comparison of the sound absorption coefficient for normal incidence for three absorbers with the same Total Construction Height (TCH), which means that they all take up equal space, as viewed from the room. Specifically, all three absorbers require 100 mm of space in front of the hard surface that needs to be acoustically treated. However, one of them is 100 mm thick and is mounted directly onto the hard surface without the airgap, and the other two have different combinations of thickness and airgap. All three end up with the TCH of 100 mm: 100 mm material + 0 mm airgap; 50 mm material + 50 mm airgap; 25 mm material + 75 mm airgap.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-8.svg alt="Figure 8. Absorption coefficient for three porous absorbers with the same total construction height of 100 mm, but installed differently [3]."><figcaption class=caption>Figure 8. Absorption coefficient for three porous absorbers with the same total construction height of 100 mm, but installed differently [3].</figcaption></figure>
<p>
   Up to this point, the behaviour of porous absorbers has been examined only for normal incidence of the sound wave. In real life, sound waves hit absorbers from all directions (given that the sound field is dominantly diffuse). The absorption coefficient varies with the angle of incidence of the sound wave on the absorber surface, as the path of the sound wave through the absorber becomes longer and longer as the angle of incidence increases. <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-9.svg data-img-alt="Figure 9. Absorption coefficient for porous absorbers, depending on the angle of incidence for the sound wave in degrees (0 – normal incidence, 90 – grazing incidence) [3].">Figure 9</a> illustrates the absorption coefficient for various angles of incidence. Absorption at high frequencies does not decrease dramatically with increasing angle of incidence, but there is a substantial increase in low-frequency absorption. When the incident angle approaches 90<span class=tcrm-1000>°</span>, the sound wave only grazes the absorber (the so-called “grazing incidence”), and in these conditions it basically loses its function. The manufacturers commonly measure the absorption coefficient of their materials in diffuse sound field conditions, i.e. in reverberation chambers, thus getting the values of the absorption coefficient averaged over all possible angles of incidence. These values can be used for acoustic simulations in real-life applications of absorbers, since the sound field in closed rooms is predominantly diffuse, i.e. similar to the one that is formed during measurements in the reverberation chamber.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-9.svg alt="Figure 9. Absorption coefficient for porous absorbers, depending on the angle of incidence for the sound wave in degrees (0 – normal incidence, 90 – grazing incidence) [3]."><figcaption class=caption>Figure 9. Absorption coefficient for porous absorbers, depending on the angle of incidence for the sound wave in degrees (0 – normal incidence, 90 – grazing incidence) [3].</figcaption></figure>
<h3 class=sectionHead>Membrane absorbers</h3>
<p>
   Membrane or panel absorbers are characterized with a different underlying physical principle of sound absorption. Their behaviour is based on the classical mass-spring oscillating system, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-10.svg data-img-alt="Figure 10. Membrane absorbers presented as a mass-spring system (left). Panel absorber (right) [4].">Figure 10</a>. They are efficient in absorbing sound mostly around the resonance frequency of such systems. A panel absorber consists of a thin plate of surface mass m’’ fastened on a rigid frame in front of a rigid backing surface with the surface mass m’. There is an air cavity of thickness d which can be partially of fully filled with a damping layer, usually some porous materials.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-10.svg alt="Figure 10. Membrane absorbers presented as a mass-spring system (left). Panel absorber (right) [4]."><figcaption class=caption>Figure 10. Membrane absorbers presented as a mass-spring system (left). Panel absorber (right) [4].</figcaption></figure>
<p>The resonance frequency for membrane absorbers is determined by the following expression:
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mi>d</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <mn>1900</mn>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mi>d</mi>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
   </math>
   is the resonance frequency in Hz,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
   </math>
   is the dynamic stiffness in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>MN</mi>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   is the surface mass of the membrane plate mounted in front of a much heavier plate or object, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   is the distance from the plate to the rigid backing surface (wall, ceiling…), expressed in millimeters. This is valid when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   , meaning that the backing wall or ceiling is much heavier that the thin panel of the membrane absorber.<br class=newline>Typical examples of such absorbers are gypsum boards or thin wooden panels mounted in front of concrete or brick walls or suspended below concrete ceilings. These constructions can be upgraded, and their sound absorption properties can be improved by installing a layer of porous material between the plate and the wall. The air cavity can be fully or partially filled with the porous material.<br class=newline>The main difference between the basic construction and the improved alternative is that the empty cavity between the plate and the wall leads to strongly resonant behaviour of the absorber. This means that the absorption coefficient is displayed with a narrow and steep bell curve, indicating that the absorber is capable of absorbing most of the sound energy in a narrow frequency band around the resonance frequency. On the other hand, the porous material added into the cavity acts as acoustic damping. In this case, the sound absorption coefficient is displayed with a wider and flatter curve, indicating that it doesn’t absorb sound energy as effectively as the empty absorber, but its operating frequency range is broader. To calculate the sound absorption coefficient, the normalized characteristic impedance of the absorber is defined as:
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mi>s</mi>
                     </mrow>
                  </msqrt>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>d</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(13)
</table>
<p>The normalized characteristic impedance of the absorber depends on the surface mass of the resonator plate and on the distance of the plate from the rigid and heavy back wall. The absorption coefficient for normal sound incidence can be calculated using the following expression:
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>α</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <msup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>+</mo>
                        <mn>1</mn>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <msubsup>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msubsup>
                        <mi>F</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>max</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <msubsup>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>R</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msubsup>
                           </mrow>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-bin>+</mo>
                              <mn>1</mn>
                           </mrow>
                        </mfrac>
                        <mi>F</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(14)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>F</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   In Eq. 14,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   is the amplitude ratio of the complex reflection factor and the normalized air impedance, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
   </math>
   is the maximum possible absorption coefficient at the resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
   </math>
   . The calculated absorption coefficient is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-11.svg data-img-alt="Figure 11. Absorption coefficient of membrane absorbers depending on the normalized characteristic impedance of the absorber (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msubsup><mrow><mi>Z</mi></mrow><mrow><mi>R</mi></mrow><mrow><mi>′</mi></mrow></msubsup></math>), optimally damped (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msup><mrow><mi>r</mi></mrow><mrow><mi>′</mi></mrow></msup><mo class=MathClass-rel>=</mo><mn>1</mn></math>) [5].">Figure 11</a>. In terms of efficiency of sound absorption, purely resonant absorbers (see next section) are preferred, as it is easier to tune their resonance frequency. However, membrane absorbers are often used inherently, i.e. as an unintentional by-product of current building practice. For example, many interior walls and ceilings are made of gypsum boards mounted in front of massive walls or suspended from ceilings. These building constructions already behave as membrane absorbers with non-negligible sound absorption at low frequencies.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-11.svg alt="Figure 11. Absorption coefficient of membrane absorbers depending on the normalized characteristic impedance of the absorber (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msubsup><mrow><mi>Z</mi></mrow><mrow><mi>R</mi></mrow><mrow><mi>′</mi></mrow></msubsup></math>), optimally damped (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msup><mrow><mi>r</mi></mrow><mrow><mi>′</mi></mrow></msup><mo class=MathClass-rel>=</mo><mn>1</mn></math>) [5]."><figcaption class=caption>Figure 11. Absorption coefficient of membrane absorbers depending on the normalized characteristic impedance of the absorber (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msubsup><mrow><mi>Z</mi></mrow><mrow><mi>R</mi></mrow><mrow><mi>′</mi></mrow></msubsup></math>), optimally damped (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msup><mrow><mi>r</mi></mrow><mrow><mi>′</mi></mrow></msup><mo class=MathClass-rel>=</mo><mn>1</mn></math>) [5].</figcaption></figure>
<h3 class=sectionHead>Resonant absorbers</h3>
<p>
   Resonant absorbers are based on the principle of Helmholtz resonators. The most common constructions involve an air cavity between a massive wall or ceiling and a much thinner plate, much like membrane absorbers. However, there is a significant difference: the thin plate has one or more openings that connect the air cavity to the outer space. An example of such absorbers is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-12.svg data-img-alt="Figure 12. The construction principle of resonant absorbers.">Figure 12</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-12.svg alt="Figure 12. The construction principle of resonant absorbers."><figcaption class=caption>Figure 12. The construction principle of resonant absorbers.</figcaption></figure>
<p>The operating principle of the resonant absorber is based on the spring-mass system, but there is a difference compared to the membrane absorber. Specifically, the air contained within the volume of the opening behaves as the acoustical mass that oscillates, and the air volume inside the air cavity is basically an air cushion that acts like a spring. Any construction or object that exhibits a narrow opening that leads to a vessel with a given air volume acts as a Helmholtz resonator and, therefore, is able to operate as a resonant absorber. For example, a bottle with a narrow neck or a vented box loudspeaker (when it is not working) exhibits sound absorption properties as resonant absorbers.<br class=newline>The resonance frequency of resonant absorbers is given by the following expression:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>170</mn>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>V</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>d</mi>
                        <mo class=MathClass-bin>+</mo>
                        <mn>2</mn>
                        <mi mathvariant=italic>Δt</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(16)
</table>
<p>
   In Eq. 16,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the resonance frequency of the absorber in Hz,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is the surface of the opening in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   is the air volume in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   is the thickness of the front plate of the resonator in cm, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mi mathvariant=italic>Δt</mi>
   </math>
   is the correction, expressed in cm as well, that is added to the plate thickness due to the opening curvature and the non-linear effects that occur at the edges of the opening. This correction is given in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/table-2.svg data-img-alt="Table 2. Correction for resonant absorber opening.">Table 2</a>. Typical examples of Helmholtz resonators with round perforation and slits, both of which are often used in practical cases, can be seen in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-13.svg data-img-alt="Figure 13. Example of Helmholtz resonators. Left – round openings; right - slits [3].">Figure 13</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 2. Correction for resonant absorber opening.</figcaption>
      <img src=${config.ASSETS_URL}/05/08/theory/table-2.svg alt="Table 2. Correction for resonant absorber opening.">
   </figure>
</div>
<figure class=figure>
   <p>
      <img src=${config.ASSETS_URL}/05/08/theory/figure-13.svg alt="Figure 13. Example of Helmholtz resonators. Left – round openings; right - slits [3].">
      <figcaption class=caption>Figure 13. Example of Helmholtz resonators. Left – round openings; right - slits [3].</figcaption>
</figure>
<p>
   To broaden the operating frequency range of resonant absorbers built as Helmholtz resonators, their viscous losses must be increased. A certain amount of losses already exists due to the movement of air through the holes or the slits on the panel. However, these losses are not high enough to make a difference, unless the holes or slits are very small. If so, a very special type of absorbers is formed, i.e. the microperforated absorbers (see next section), where the slits or holes typically have a size of 0.5 mm width/diameter, or even less.<br class=newline>To increase the losses of ordinary resonant absorbers and broaden their operating frequency range, an additional absorptive layer is added into the cavity of a Helmholtz resonator, usually directly onto the back side of the perforated panel. This additional layer is typically made of mineral or glass wool. Alternatively, thin nonwoven fabric can be glued on the back side of the panel, as is the often case for perforated gypsum board panels. Cross sections of different resonant absorber types are shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-14.svg alt="Figure 14. Cross section through different types of resonant absorbers [3].">Figure 14</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-14.svg alt="Figure 14. Cross section through different types of resonant absorbers [3]."><figcaption class=caption>Figure 14. Cross section through different types of resonant absorbers [3].</figcaption></figure>
<p>
   It is not trivial to make a good analytical model for calculating the absorption coefficient of resonant absorbers. However, there are fairly simple equations that can provide a good estimation of typical absorption coefficients within the frequency region of interest. <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-15.svg data-img-alt="Figure 15. The change of the normal-incidence absorption coefficient of a resonant absorber, depending on the flow resistance of the porous layer added into its cavity [3].">Figure 15</a> shows how the normal-incidence absorption coefficient of the absorber changes depending on the flow resistance of a layer of porous material added on the back the perforated panel of the absorber. This kind of absorbers has a prominent peak of the absorption coefficient at the resonance frequency. Due to their usual size and other physical parameters, these absorbers are great mid-frequency absorbers.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-15.svg alt="Figure 15. The change of the normal-incidence absorption coefficient of a resonant absorber, depending on the flow resistance of the porous layer added into its cavity [3]."><figcaption class=caption>Figure 15. The change of the normal-incidence absorption coefficient of a resonant absorber, depending on the flow resistance of the porous layer added into its cavity [3].</figcaption></figure>
<p>
   If the percentage of perforation of the absorber front panel increases, with all other parameters fixed, the resulting absorption coefficient tends to increase toward high frequencies, and to decrease at low frequencies, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-16.svg data-img-alt="Figure 16. The change of the absorption coefficient of resonant absorbers with the percentage of perforation of the front panel [8].">Figure 16</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-16.svg alt="Figure 16. The change of the absorption coefficient of resonant absorbers with the percentage of perforation of the front panel [8]."><figcaption class=caption>Figure 16. The change of the absorption coefficient of resonant absorbers with the percentage of perforation of the front panel [8].</figcaption></figure>
<p>
   Finally, the effectiveness of the porous material in the air cavity of the absorber greatly depends on its exact placement, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-17.svg data-img-alt="Figure 17. The influence of the placement of the porous layer inside the air cavity of the porous absorber on its sound absorption properties [6].">Figure 17</a>. The air cavity can be fully or partially filled with porous material. If R‘ denotes the flow resistivity of a single hole on the perforated panel, which can be directly related to the introduced damping, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   the flow resistivity of the porous material, it becomes clear that both parameters are greater if the porous layer is placed closer to the perforated plate. Therefore, the layer of porous material is the most effective if mounted directly to the back side of the perforated plate, i.e. as close to the holes/slits as physically possible.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-17.svg alt="Figure 17. The influence of the placement of the porous layer inside the air cavity of the porous absorber on its sound absorption properties [6]."><figcaption class=caption>Figure 17. The influence of the placement of the porous layer inside the air cavity of the porous absorber on its sound absorption properties [6].</figcaption></figure>
<h3 class=sectionHead>Other types of absorbers</h3>
<h4 class=subsectionHead>Microperforated absorbers</h4>
<p>
   Microperforated absorbers are a special type of resonant absorbers that consist of very thin front plates or foils (up to 8 mm thick) with very small openings (bores), typically between 0.3 and 2.0 mm. The front plate of such an absorber also encloses the air cavity towards a massive backing construction such as a wall or a ceiling. The basic construction of this kind of resonator is again a Helmholtz resonator that functions on a mass-spring system. Although the operating principle is the same as the one for resonant absorbers, microperforated resonators are more efficient in the high-frequency range. Moreover, the porous layer behind the perforated front plate is not required, as the viscous losses that arise due to the air moving through the micro-perforations are already high enough to cause considerable loss of sound energy, which is manifested as an increase of sound absorption.<br class=newline>The operating principle of microperforated absorbers is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-18.svg data-img-alt="Figure 18. The change of friction (and viscous losses) with hole size. Smaller holes lead to greater friction.">Figure 18</a>. Sound energy is dissipated due to the increased friction of the air molecules in the very small holes in the front plate of the absorber.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-18.svg alt="Figure 18. The change of friction (and viscous losses) with hole size. Smaller holes lead to greater friction."><figcaption class=caption>Figure 18. The change of friction (and viscous losses) with hole size. Smaller holes lead to greater friction.</figcaption></figure>
<p>
   The application of microperforated absorbers in the form of transparent foils or plates is particularly interesting, since they can be mounted in rooms where other kinds of absorbers would not be acceptable, e.g.in old buildings and rooms with artistic paintings on the walls or ceiling, etc. <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-19.svg data-img-alt="Figure 19. Typical frequency-dependent absorption coefficient for two examples of microperforated absorbers [8].">Figure 19</a> shows the typical absorption coefficient for microperforated absorbers. Although the maximum absorption coefficient does not reach unity (equaling to 100% of absorbed sound energy), one needs to bear in mind that these absorbers do not include an additional layer of porous material behind the perforated plate, unlike the majority of classical resonant absorbers.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-19.svg alt="Figure 19. Typical frequency-dependent absorption coefficient for two examples of microperforated absorbers [8]."><figcaption class=caption>Figure 19. Typical frequency-dependent absorption coefficient for two examples of microperforated absorbers [8].</figcaption></figure>
<h3 class=sectionHead>Concluding remarks</h3>
<p>
   A good summary on the efficiency of various types of sound absorbers is given in <a href="#" data-img-src=${config.ASSETS_URL}/05/08/theory/figure-20.svg data-img-alt="Figure 20. The qualitative change of the absorption coefficient depending on the percentage of perforation of the front plate of the absorber [7].">Figure 20</a>. If a thin plate is mounted in front of a hard wall or ceiling, and a layer of porous material is added into the air cavity between the plate and the wall, the change of the absorption coefficient can be observed depending on the percentage of perforation of the front plate. With no front plate to begin with (100 percent of perforation), the maximum absorber efficiency is found at high frequencies. As the percentage of perforation of the front plate decreases (fewer and fewer holes, and the holes getting smaller and smaller), the maximum efficiency of the absorber shifts from the high-frequency range over mid-frequency range, and ultimately down to the low-frequency range, when the plate remains completely unperforated (0 percent of perforation).<br class=newline>Depending on the acoustical design goals and needs, absorbers can be chosen from a wide variety of types to get optimum acoustics of the space that is undergoing acoustic treatment. More information on the various acoustic design elements can be found in <a href="${linkLesson0515}" target="_blank">Lesson 5.15</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/08/theory/figure-20.svg alt="Figure 20. The qualitative change of the absorption coefficient depending on the percentage of perforation of the front plate of the absorber [7]."><figcaption class=caption>Figure 20. The qualitative change of the absorption coefficient depending on the percentage of perforation of the front plate of the absorber [7].</figcaption></figure>
<p>
<h3>Glossary</h3>
<dl>
  <dt>TCH
  <dd>Total Construction Height
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-absorbers>[1]
   <dd class=thebibliography id=bib-1>
      <p>Trevor J. Cox and Peter D’Antonio. <span class=cmti-10>Acoustic Absorbers and Diffusers</span>. 2. ed. London, New York: Taylor & Francis, 2009. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780415471749.
   <dt class=thebibliography id=X0-master>[2]
   <dd class=thebibliography id=bib-2>
      <p>F. Alton Everest and Ken C. Pohlmann. <span class=cmti-10>Master Handbook of Acoustics</span>. 5. ed. New York: McGraw Hill, 2009. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780071603324.
   <dt class=thebibliography id=X0-applied>[3]
   <dd class=thebibliography id=bib-3>
      <p>Helmut Fuchs. <span class=cmti-10>Applied Acoustics: Concepts, Absorbers, and Silencers for </span><span class=cmti-10>Acoustical Comfort and Noise Control</span>. 1. ed. Berlin: Springer, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9783642293665.
   <dt class=thebibliography id=X0-small>[4]
   <dd class=thebibliography id=bib-4>
      <p>Mendel Kleiner and Jiri Tichy. <span class=cmti-10>Acoustics of Small Rooms</span>. 1. ed. Boca Raton: CRC Press, 2014. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9781138072831.
   <dt class=thebibliography id=X0-room>[5]
   <dd class=thebibliography id=bib-5>
      <p>Heinrich Kuttruff. <span class=cmti-10>Room Acoustics</span>. 5. ed. New York: Spon Press, 2009. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780367870997.
   <dt class=thebibliography id=X0-architectural>[6]
   <dd class=thebibliography id=bib-6>
      <p>Marshall Long. <span class=cmti-10>Architectural Acoustics</span>. 1. ed. Amsterdam: Elsevier Academic Press, 2006. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780124555518.
   <dt class=thebibliography id=X0-building>[7]
   <dd class=thebibliography id=bib-7>
      <p>Tor Erik Vigran. <span class=cmti-10>Building Acoustics</span>. 1. ed. London, New York: Taylor & Francis, 2008. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780367865214.
   <dt class=thebibliography id=X0-formeln>[8]
   <dd class=thebibliography id=bib-8>
      <p>Wolfgang M. Willems et al. <span class=cmti-10>Formeln und Tabellen Bauphysik</span>. 2. ed. Wiesbaden: Vieweg+Teubner, 2010.
</dl>`;

export default theory;
